import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Injects a <link rel="alternate" type="text/markdown" href="<path>.md">
 * into the document head for the current route, so AI crawlers can
 * auto-discover the Markdown mirror of every page.
 *
 * Only paths that actually have a .md mirror get a link.
 */
const STATIC_MIRRORS = new Set([
  "/", "/about", "/solutions", "/industries", "/projects",
  "/ask-arif", "/insights", "/learning-center", "/media", "/partners", "/contact",
]);

function mirrorPathFor(pathname: string): string | null {
  // Normalize: strip trailing slash (except root).
  const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (STATIC_MIRRORS.has(p)) {
    return p === "/" ? "/index.md" : `${p}.md`;
  }
  // Dynamic mirrors: /solutions/{slug}, /industries/{slug}, /ask-arif/{slug}
  if (/^\/solutions\/[^/]+$/.test(p)) return `${p}.md`;
  if (/^\/industries\/[^/]+$/.test(p)) return `${p}.md`;
  if (/^\/ask-arif\/[^/]+$/.test(p) && !p.startsWith("/ask-arif/category/")) return `${p}.md`;
  return null;
}

export function MarkdownAlternateLink() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const head = document.head;
    // Remove any previous mirror link we added.
    head.querySelectorAll('link[data-md-mirror="1"]').forEach((el) => el.remove());

    const mirror = mirrorPathFor(pathname);
    if (!mirror) return;

    const link = document.createElement("link");
    link.rel = "alternate";
    link.type = "text/markdown";
    link.href = mirror;
    link.setAttribute("data-md-mirror", "1");
    head.appendChild(link);
  }, [pathname]);

  return null;
}
